import { createI18n } from 'vue-i18n';
import * as Sentry from '@sentry/vue';
import {
    contentLanguageForLocale,
    isContentLanguage,
    isSupportedLocale,
    SUPPORTED_LOCALE_CODES,
    LOCALE_MESSAGES,
    localeForContentLanguage,
} from '@/locales/supportedLocales.ts';
import type { ContentLanguage, Locale } from '@/locales/supportedLocales.ts';

const LEGACY_LOCALE_ALIASES = {
    fr: 'fr-CA',
    en: 'en-CA',
} as const;

const normalizeCompatibleLocale = (locale: unknown): unknown =>
    isContentLanguage(locale) ? LEGACY_LOCALE_ALIASES[locale] : locale;

const resolveLocale = (locale: unknown): Locale => {
    const normalizedLocale = normalizeCompatibleLocale(locale);

    return isSupportedLocale(normalizedLocale) ? normalizedLocale : 'en-CA';
};

const resolveContentLanguage = (locale: unknown): ContentLanguage => {
    if (isContentLanguage(locale)) {
        return locale;
    }

    return isSupportedLocale(locale) ? contentLanguageForLocale(locale) : 'en';
};

const localeMessages = {
    ...LOCALE_MESSAGES,
    fr: LOCALE_MESSAGES['fr-CA'],
    en: LOCALE_MESSAGES['en-CA'],
};

const compatibleLocaleCodes = [...SUPPORTED_LOCALE_CODES, 'fr', 'en'] as const;

const resolveInitialLocale = (): Locale => {
    if (typeof localStorage === 'undefined') {
        return 'en-CA';
    }

    const storedLocale = localStorage.getItem('currentLocale');

    let locale: Locale;

    if (isContentLanguage(storedLocale)) {
        locale = localeForContentLanguage(storedLocale, 'CA') ?? 'en-CA';
    } else if (isSupportedLocale(storedLocale)) {
        locale = storedLocale;
    } else if (navigator.language.includes('fr')) {
        locale = 'fr-CA';
    } else {
        locale = 'en-CA';
    }

    localStorage.setItem('currentLocale', locale);

    return locale;
};

const numberFormats = {
    USD: {
        style: 'currency',
        currency: 'USD',
        currencyDisplay: 'narrowSymbol',
    },
    CAD: {
        style: 'currency',
        currency: 'CAD',
        currencyDisplay: 'narrowSymbol',
    },
    decimal: {
        style: 'decimal',
    },
    km: {
        style: 'unit',
        unit: 'kilometer',
    },
    mi: {
        style: 'unit',
        unit: 'mile',
    },
    hr: {
        style: 'unit',
        unit: 'hour',
    },
} as const;

const dateFormats = {
    short: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    },
    long: {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    },
} as const;

const i18n = createI18n({
    // Composition mode avoids the beforeCreate mixin that collides with
    // @vue/compat's proxy set trap when setup() exposes `t` (ASPD-28063).
    // globalInjection registers $t/$d/$n on app.config.globalProperties so
    // Options API templates can still use {{ $t(...) }}.
    legacy: false,
    globalInjection: true,
    locale: resolveInitialLocale(),
    fallbackLocale: false,
    // `fr` and `en` are internal rollout aliases only. Runtime locale state is
    // normalized to a regional locale, while legacy explicit overrides keep
    // resolving until their callers are migrated.
    messages: localeMessages,
    numberFormats: Object.fromEntries(
        compatibleLocaleCodes.map((locale) => [locale, numberFormats])
    ),
    datetimeFormats: Object.fromEntries(
        compatibleLocaleCodes.map((locale) => [locale, dateFormats])
    ),
    warnHtmlMessage: false,
    missingWarn: false,
    fallbackWarn: false,
});

i18n.global.missing = (locale, key) => {
    const message = `Missing translation: ${String(key)} (${String(locale)})`;
    // eslint-disable-next-line no-console
    console.warn(message);
    Sentry.captureMessage(message, 'info');
};

// Proxy vue-i18n v8 API onto the i18n instance for backward compatibility.
// In v10, methods like t/te live on i18n.global, but the codebase calls
// i18n.t(...) directly (entities, utils, store actions). Components are
// unaffected because $t is injected by the plugin's install().
interface LegacyI18nMethods {
    t: (typeof i18n.global)['t'];
    te: (typeof i18n.global)['te'];
    d: (typeof i18n.global)['d'];
    n: (typeof i18n.global)['n'];
    tm: (typeof i18n.global)['tm'];
    rt: (typeof i18n.global)['rt'];
    locale: string;
    fallbackLocale: string | false;
    getLocaleMessage: (typeof i18n.global)['getLocaleMessage'];
    setLocaleMessage: (typeof i18n.global)['setLocaleMessage'];
    getNumberFormat: (typeof i18n.global)['getNumberFormat'];
    setNumberFormat: (typeof i18n.global)['setNumberFormat'];
    getContentLanguage: (locale?: unknown) => ContentLanguage;
    getLocale: (locale?: unknown) => Locale;
    setLocale: (locale: unknown) => Locale;
}

type LegacyCompatibleI18n = typeof i18n & LegacyI18nMethods;
const compatI18n = i18n as LegacyCompatibleI18n;
const compatGlobal = i18n.global as unknown as Record<string, unknown>;

// vue-i18n's composition global exposes locale as a writable computed ref.
// Legacy tests and callers assign a string directly to i18n.global.locale,
// which would otherwise replace that ref. Preserve the ref and normalize the
// temporary base-language assignment at this single compatibility boundary.
const globalLocaleRef = compatGlobal.locale;
if (typeof globalLocaleRef === 'object' && globalLocaleRef !== null && 'value' in globalLocaleRef) {
    Object.defineProperty(compatGlobal, 'locale', {
        configurable: true,
        enumerable: true,
        get: () => globalLocaleRef,
        set: (locale: unknown) => {
            (globalLocaleRef as { value: unknown }).value = normalizeCompatibleLocale(locale);
        },
    });
}

const i18nMethods = ['t', 'te', 'd', 'n', 'tm', 'rt'] as const;
i18nMethods.forEach((method) => {
    if (!(method in compatI18n) && typeof compatGlobal[method] === 'function') {
        (compatI18n as unknown as Record<string, unknown>)[method] = (...args: unknown[]) =>
            (compatGlobal[method] as (...params: unknown[]) => unknown)(...args);
    }
});

const i18nProps = ['locale', 'fallbackLocale'] as const;
i18nProps.forEach((prop) => {
    Object.defineProperty(compatI18n, prop, {
        configurable: true,
        enumerable: true,
        get: () => {
            const value = compatGlobal[prop];

            if (typeof value === 'object' && value !== null && 'value' in value) {
                return (value as { value: unknown }).value;
            }

            return value;
        },
        set: (val: unknown) => {
            const currentValue = compatGlobal[prop];
            const compatibleValue = prop === 'locale' ? normalizeCompatibleLocale(val) : val;

            if (
                typeof currentValue === 'object' &&
                currentValue !== null &&
                'value' in currentValue
            ) {
                (currentValue as { value: unknown }).value = compatibleValue;
                return;
            }

            compatGlobal[prop] = compatibleValue;
        },
    });
});

const i18nExtraMethods = [
    'getLocaleMessage',
    'setLocaleMessage',
    'getNumberFormat',
    'setNumberFormat',
] as const;
i18nExtraMethods.forEach((method) => {
    if (!(method in compatI18n) && typeof compatGlobal[method] === 'function') {
        (compatI18n as unknown as Record<string, unknown>)[method] = (...args: unknown[]) =>
            (compatGlobal[method] as (...params: unknown[]) => unknown)(...args);
    }
});

const getContentLanguage = (locale: unknown = compatI18n.locale): ContentLanguage =>
    resolveContentLanguage(locale);
const getLocale = (locale: unknown = compatI18n.locale): Locale => resolveLocale(locale);
const setLocale = (locale: unknown): Locale => {
    const resolvedLocale = resolveLocale(locale);

    if (
        typeof globalLocaleRef === 'object' &&
        globalLocaleRef !== null &&
        'value' in globalLocaleRef
    ) {
        (globalLocaleRef as { value: unknown }).value = resolvedLocale;
    } else {
        compatGlobal.locale = resolvedLocale;
    }

    return resolvedLocale;
};

compatI18n.getContentLanguage = getContentLanguage;
compatI18n.getLocale = getLocale;
compatI18n.setLocale = setLocale;
compatGlobal.getContentLanguage = getContentLanguage;
compatGlobal.getLocale = getLocale;
compatGlobal.setLocale = setLocale;

const originalInstall = compatI18n.install.bind(compatI18n);
const installWithLocaleHelpers: typeof compatI18n.install = (app, ...options) => {
    const result = originalInstall(app, ...options);
    const componentI18n = app.config.globalProperties.$i18n as Record<string, unknown> | undefined;

    if (componentI18n) {
        componentI18n.getContentLanguage = getContentLanguage;
        componentI18n.getLocale = getLocale;
        componentI18n.setLocale = setLocale;
    }

    return result;
};
compatI18n.install = installWithLocaleHelpers;

if ((import.meta as any).hot) {
    (import.meta as any).hot.accept(
        ['@/locales/fr-CA/index.js', '@/locales/en-CA/index.js'],
        () => {
            Object.entries(LOCALE_MESSAGES).forEach(([locale, messages]) => {
                i18n.global.setLocaleMessage(locale, messages);
            });
            i18n.global.setLocaleMessage('fr', LOCALE_MESSAGES['fr-CA']);
            i18n.global.setLocaleMessage('en', LOCALE_MESSAGES['en-CA']);
        }
    );
}

export default compatI18n;
